import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useSettings } from "@/lib/settings";
import { useLocalizedSettings } from "@/lib/localizedSettings";
import { useT } from "@/lib/i18n";

function Section({ title, children }) {
  if (!children) return null;
  return (
    <div className="mt-10">
      <h2 className="mono text-[11px] uppercase tracking-[0.25em] text-[#FF4B00]">
        {title}
      </h2>
      <div className="mt-3 space-y-1 text-sm leading-relaxed text-white/80">
        {children}
      </div>
    </div>
  );
}

export default function Impressum() {
  const s = useLocalizedSettings();
  const t = useT();

  return (
    <div data-testid="impressum-page" className="min-h-screen bg-[#09090B]">
      <Header />

      <main className="mx-auto max-w-3xl px-6 py-16 lg:px-10 lg:py-24">
        <Link
          to="/"
          data-testid="back-home"
          className="mono inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/50 transition-colors hover:text-[#FF4B00]"
        >
          <ArrowLeft className="h-3 w-3" strokeWidth={2.5} />
          {t("back")}
        </Link>

        <span className="tag-orange mt-8 inline-block">{t("imp.tag")}</span>
        <h1 className="mt-4 text-4xl font-black uppercase leading-[0.95] sm:text-5xl">
          {t("imp.title")}
        </h1>
        <p className="mono mt-3 text-[11px] uppercase tracking-[0.25em] text-white/50">
          {t("imp.legalRef")}
        </p>

        <Section title={t("imp.provider")}>
          <p className="font-semibold text-white">{s.workshop_name}</p>
          {s.imprint_owner && <p>{s.imprint_owner}</p>}
          {s.address_line1 && <p>{s.address_line1}</p>}
          {s.address_line2 && <p>{s.address_line2}</p>}
        </Section>

        <Section title={t("imp.contact")}>
          {s.phone && (
            <p>
              {t("imp.phone")}{" "}
              <a
                href={`tel:${s.phone.replace(/\s+/g, "")}`}
                className="text-white hover:text-[#FF4B00]"
              >
                {s.phone}
              </a>
            </p>
          )}
          {s.email && (
            <p>
              {t("imp.email")}{" "}
              <a
                href={`mailto:${s.email}`}
                className="text-white hover:text-[#FF4B00]"
              >
                {s.email}
              </a>
            </p>
          )}
        </Section>

        {s.imprint_ustid && (
          <Section title={t("imp.ustid")}>
            <p className="mono">{s.imprint_ustid}</p>
          </Section>
        )}

        {s.imprint_register && (
          <Section title={t("imp.register")}>
            <p>{s.imprint_register}</p>
          </Section>
        )}

        <Section title={t("imp.responsible")}>
          <p>
            {s.imprint_responsible || s.imprint_owner || s.workshop_name}
            {(s.address_line1 || s.address_line2) && (
              <>
                <br />
                {s.address_line1}
                {s.address_line1 && s.address_line2 && ", "}
                {s.address_line2}
              </>
            )}
          </p>
          <p className="text-xs text-white/50">{t("imp.responsibleRef")}</p>
        </Section>

        {s.imprint_additional && (
          <Section title={t("imp.additional")}>
            <p className="whitespace-pre-line">{s.imprint_additional}</p>
          </Section>
        )}

        <Section title={t("imp.liability")}>
          <p className="text-white/70">{t("imp.liabilityText")}</p>
        </Section>

        <Section title={t("imp.dispute")}>
          <p className="text-white/70">
            {t("imp.disputeText1")}{" "}
            <a
              href="https://ec.europa.eu/consumers/odr/"
              target="_blank"
              rel="noreferrer"
              className="text-[#FF4B00] hover:underline"
            >
              https://ec.europa.eu/consumers/odr/
            </a>
            {t("imp.disputeText2")}
          </p>
        </Section>
      </main>

      <Footer />
    </div>
  );
}
